import { useSimulationStore, SIM_START_ISO } from '@/state/simulationStore';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { eventBus } from '@/events/eventBus';
import type { SimEvent } from '@/events/simEvents';
import { SCRIPTED_EVENTS } from '@/data/scriptedEvents';
import { handleSimEvent } from '@/domain/eventHandlers';

// Simulation engine: stepper model.
// PLAY is a "next event" button, not a real-time tick. Each click jumps the
// clock to the next unfired scripted event and fires it. Demo cadence is
// driven by the facilitator, not by wall-clock time. PRD §11.

const scriptedEvents: SimEvent[] = SCRIPTED_EVENTS.slice().sort(
  (a, b) => new Date(a.triggerIso).getTime() - new Date(b.triggerIso).getTime(),
);
let firedThisRun: Set<string> = new Set();

function fireEvent(evt: SimEvent) {
  firedThisRun.add(evt.id);
  useSimulationStore.getState().recordFired(evt.id);
  eventBus.publish(evt.type, evt.payload);
  eventBus.publish('sim:event', { ...evt });
  handleSimEvent(evt);
}

function findNextUnfired(): SimEvent | null {
  for (const evt of scriptedEvents) {
    if (!firedThisRun.has(evt.id)) return evt;
  }
  return null;
}

export const simulationApi = {
  // Advance to the next scripted event. Jumps the clock to the event's
  // triggerIso and fires it. Click again for the next beat.
  // Frozen clock (persistence fast-unlock): advancing time is disabled. Kept as
  // a no-op so any lingering "next beat" call site does not throw. Fire specific
  // events with triggerEvent / triggerEventByType instead — they run against the
  // frozen DEMO_NOW.
  play() {},
  // Pause is a no-op in stepper mode but kept for API symmetry with tests.
  pause() {
    useSimulationStore.getState().setRunning(false);
  },
  setClock(_iso: string) {
    // No-op: the clock is frozen at DEMO_NOW. The store's setClock is still the
    // test seam, but the running app never moves the clock through this API.
  },
  reset() {
    firedThisRun = new Set();
    useSimulationStore.getState().reset();
    useBrainStore.getState().reset();
    useUIStore.getState().clearIncomingCalls();
    eventBus.clear();
    useBrainStore.getState().loadFixtures();
  },
  triggerEvent(eventId: string) {
    const evt = scriptedEvents.find((e) => e.id === eventId);
    if (!evt) {
      console.warn('[simulationApi] unknown event id', eventId);
      return;
    }
    fireEvent(evt);
  },
  triggerEventByType(type: SimEvent['type'], payload?: Record<string, unknown>) {
    fireEvent({
      id: `manual-${type}-${Date.now()}`,
      type,
      triggerIso: useSimulationStore.getState().clockIso,
      description: `Manual trigger: ${type}`,
      payload,
    });
  },
  jumpToScenario(scenarioId: number) {
    // Clock is frozen; keep the cursor so the console can still highlight a
    // scenario, but do not move time.
    const first = scriptedEvents.find((e) => e.scenarioId === scenarioId);
    if (first) useSimulationStore.getState().setScenarioCursor(scenarioId);
  },
  getScripted() {
    return scriptedEvents.slice();
  },
  // Returns the next event (or null if all fired). Used by the TopBar to show
  // a preview of the next beat.
  nextEvent(): SimEvent | null {
    return findNextUnfired();
  },
  upcoming(n = 5) {
    return scriptedEvents.filter((e) => !firedThisRun.has(e.id)).slice(0, n);
  },
  fired() {
    return Array.from(firedThisRun);
  },
  remaining() {
    return scriptedEvents.length - firedThisRun.size;
  },
  total() {
    return scriptedEvents.length;
  },
};

export function startSimulation() {
  // No-op in stepper mode. Kept exported so App.tsx mount/unmount stays stable.
}

export function stopSimulation() {
  simulationApi.pause();
}

export { SIM_START_ISO };
